import { EXTENSION_ARENA_ASSETS, EXTENSION_CARD_IMAGES, EXTENSION_VISUALS } from './generated/extension-visuals';

export interface VisualDefinition {
  texture?: string;
  visualScale: number;
  anchor: readonly [number, number];
  healthBarOffsetY: number;
  fallbackColor: number;
  shadow: { width: number; height: number; x?: number; y?: number; skew?: number };
  flightOffsetY?: number;
  attackCue?: AttackCueDefinition;
  projectile?: ProjectileVisualDefinition;
}

export interface AttackCueDefinition {
  durationSeconds: number;
  radius: number;
  offsetY: number;
  forward?: number;
  side?: number;
  color: number;
  hotColor: number;
}

export interface ProjectileVisualDefinition {
  shape?: 'fire' | 'bullet' | 'bomb' | 'spit' | 'cannonball' | 'arrow' | 'orb';
  texture?: string;
  width?: number;
  radius: number;
  fillColor: number;
  strokeColor: number;
  trailColor: number;
  impactColor: number;
  impactStrokeColor: number;
  impactRadius: number;
  flightOffsetY?: number;
  launchOffset?: { forward: number; side: number; settleSeconds: number };
  animation?: { pulseHz: number; pulseAmount: number };
  trail?: {
    color: number;
    coreColor: number;
    emberColor: number;
    radius: number;
    lifetimeSeconds: number;
    emitIntervalSeconds: number;
    drift: number;
  };
  impact?: {
    durationSeconds: number;
    radius: number;
    coreColor: number;
    fireColor: number;
    hotColor: number;
    shockwaveColor: number;
    smokeColor: number;
    flameCount: number;
    sparkCount: number;
  };
  targetHit?: { durationSeconds: number; color: number; hotColor: number };
}

export const VISUALS: Readonly<Record<string, VisualDefinition>> = {
  'unit.giant': { texture: '/assets/cards/giant.png', visualScale: 1.24, anchor: [0.5, 0.86], healthBarOffsetY: -74, fallbackColor: 0xc98243, shadow: { width: 42, height: 15, y: 4, skew: 25 } },
  'unit.knight': { texture: '/assets/cards/knight.png', visualScale: 1.22, anchor: [0.5, 0.86], healthBarOffsetY: -67, fallbackColor: 0xe2bd78, shadow: { width: 35, height: 14, y: 3, skew: 35 } },
  'unit.musketeer': {
    texture: '/assets/cards/musketeer.png', visualScale: 1.24, anchor: [0.5, 0.86], healthBarOffsetY: -70, fallbackColor: 0xa660b4, shadow: { width: 34, height: 13, y: 3, skew: 35 },
    attackCue: { durationSeconds: 0.12, radius: 7, offsetY: -43, forward: 25, color: 0xf6c66b, hotColor: 0xfff4c4 },
    projectile: { shape: 'bullet', radius: 4, fillColor: 0xe8d095, strokeColor: 0xfff7d5, trailColor: 0x97886e, impactColor: 0xf4d48a, impactStrokeColor: 0xffffff, impactRadius: 12, flightOffsetY: -43, launchOffset: { forward: 25, side: 0, settleSeconds: 0.1 }, targetHit: { durationSeconds: 0.14, color: 0xf4d48a, hotColor: 0xffffff } },
  },
  'unit.bomber': {
    texture: '/assets/cards/bomber.png', visualScale: 1.24, anchor: [0.5, 0.86], healthBarOffsetY: -65, fallbackColor: 0x40465a, shadow: { width: 25, height: 10, x: -2, y: 5, skew: 35 },
    attackCue: { durationSeconds: 0.15, radius: 8, offsetY: -38, forward: 10, color: 0xff9b2e, hotColor: 0xffe59a },
    projectile: { shape: 'bomb', radius: 11, fillColor: 0x252733, strokeColor: 0x080a10, trailColor: 0xff7b1c, impactColor: 0xff7b1c, impactStrokeColor: 0xffe59a, impactRadius: 30, flightOffsetY: -38, launchOffset: { forward: 10, side: 0, settleSeconds: 0.14 }, targetHit: { durationSeconds: 0.2, color: 0xff8a2a, hotColor: 0xffe7a1 } },
  },
  'unit.skeleton': { texture: '/assets/cards/skeletons.png', visualScale: 1.45, anchor: [0.5, 0.86], healthBarOffsetY: -63, fallbackColor: 0xd7e1e5, shadow: { width: 24, height: 9, x: -2, y: 5, skew: 35 } },
  'unit.minion': {
    texture: '/assets/cards/minions.png', visualScale: 1.05, anchor: [0.5, 0.86], healthBarOffsetY: -72, fallbackColor: 0x566fc1, shadow: { width: 34, height: 10, y: 10 }, flightOffsetY: -34,
    attackCue: { durationSeconds: 0.12, radius: 7, offsetY: -49, forward: 9, color: 0x78bff1, hotColor: 0xe6f7ff },
    projectile: { shape: 'spit', radius: 7, fillColor: 0x8cc6f1, strokeColor: 0xe6f7ff, trailColor: 0x67a7d8, impactColor: 0x8cc6f1, impactStrokeColor: 0xe6f7ff, impactRadius: 14, flightOffsetY: -49, launchOffset: { forward: 9, side: 0, settleSeconds: 0.1 }, targetHit: { durationSeconds: 0.16, color: 0x8cc6f1, hotColor: 0xe6f7ff } },
  },
  'building.cannon': {
    texture: '/assets/cards/cannon.png', visualScale: 1.24, anchor: [0.5, 0.82], healthBarOffsetY: -68, fallbackColor: 0x746151, shadow: { width: 54, height: 21, x: -3, y: 9, skew: 10 },
    attackCue: { durationSeconds: 0.14, radius: 9, offsetY: -39, forward: 24, color: 0xbbb0a2, hotColor: 0xffffff },
    projectile: { shape: 'cannonball', radius: 9, fillColor: 0x343a44, strokeColor: 0xaeb8bd, trailColor: 0x6e747c, impactColor: 0xd09b58, impactStrokeColor: 0xffe0a1, impactRadius: 20, flightOffsetY: -39, launchOffset: { forward: 24, side: 0, settleSeconds: 0.1 }, targetHit: { durationSeconds: 0.18, color: 0xd09b58, hotColor: 0xffe0a1 } },
  },
  'tower.princess': {
    visualScale: 1.35, anchor: [0.5, 0.8], healthBarOffsetY: -66, fallbackColor: 0xd6ad52, shadow: { width: 70, height: 27, x: -3, y: 7, skew: 13 },
    attackCue: { durationSeconds: 0.1, radius: 6, offsetY: -59, forward: 18, color: 0xecd27c, hotColor: 0xffffff },
    projectile: { shape: 'arrow', radius: 3, fillColor: 0xecd27c, strokeColor: 0xffffff, trailColor: 0xc0a85d, impactColor: 0xf1d77f, impactStrokeColor: 0xffffff, impactRadius: 12, flightOffsetY: -59, launchOffset: { forward: 18, side: 0, settleSeconds: 0.09 }, targetHit: { durationSeconds: 0.14, color: 0xf1d77f, hotColor: 0xffffff } },
  },
  'tower.king': {
    visualScale: 1.45, anchor: [0.5, 0.82], healthBarOffsetY: -80, fallbackColor: 0xf0c05b, shadow: { width: 78, height: 30, x: -4, y: 6, skew: 13 },
    attackCue: { durationSeconds: 0.14, radius: 10, offsetY: -70, forward: 20, color: 0xffb052, hotColor: 0xffefba },
    projectile: { shape: 'cannonball', radius: 10, fillColor: 0x343a44, strokeColor: 0xaeb8bd, trailColor: 0x6e747c, impactColor: 0xd09b58, impactStrokeColor: 0xffe0a1, impactRadius: 22, flightOffsetY: -70, launchOffset: { forward: 20, side: 0, settleSeconds: 0.1 }, targetHit: { durationSeconds: 0.18, color: 0xd09b58, hotColor: 0xffe0a1 } },
  },
  'spell.fireball': {
    texture: '/assets/cards/fireball.png', visualScale: 0.85, anchor: [0.5, 0.5], healthBarOffsetY: 0, fallbackColor: 0xff6b26, shadow: { width: 24, height: 8, y: 10 },
    projectile: { shape: 'fire', radius: 13, fillColor: 0xff6b26, strokeColor: 0xffe2a3, trailColor: 0xff8a2a, impactColor: 0xff6b26, impactStrokeColor: 0xffe2a3, impactRadius: 42, flightOffsetY: -20, animation: { pulseHz: 8, pulseAmount: 0.07 }, trail: { color: 0xff6b26, coreColor: 0xffb342, emberColor: 0xffef9a, radius: 10, lifetimeSeconds: 0.3, emitIntervalSeconds: 0.03, drift: 10 }, targetHit: { durationSeconds: 0.22, color: 0xff6b26, hotColor: 0xffef9a } },
  },
  ...EXTENSION_VISUALS,
};

export const CARD_IMAGES: Readonly<Record<string, string>> = {
  giant: '/assets/cards/giant.png', knight: '/assets/cards/knight.png', musketeer: '/assets/cards/musketeer.png', bomber: '/assets/cards/bomber.png',
  skeletons: '/assets/cards/skeletons.png', minions: '/assets/cards/minions.png', cannon: '/assets/cards/cannon.png', fireball: '/assets/cards/fireball.png',
  ...EXTENSION_CARD_IMAGES,
};

export const ARENA_BACKGROUND = '/assets/arena/towerarena.webp';
export const ARENA_TOWER_PLATFORM = '/assets/arena/tower-platform.png';
export const ARENA_ASSETS: Readonly<Record<string, string>> = {
  'arena.basecamp.background': ARENA_BACKGROUND,
  'arena.shared.tower-platform': ARENA_TOWER_PLATFORM,
  ...EXTENSION_ARENA_ASSETS,
};

export function projectileVisualFor(visualKey: string): ProjectileVisualDefinition | undefined {
  const exact = VISUALS[visualKey]?.projectile;
  if (exact) return exact;
  const suffix = '.projectile';
  return visualKey.endsWith(suffix) ? VISUALS[visualKey.slice(0, -suffix.length)]?.projectile : undefined;
}
